import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import { mastermindSpacesService } from "@/services/mastermindSpacesService";
import { 
  FileSearch, Target, Users, TrendingUp, BookOpen, Lightbulb, 
  ArrowRight, Sparkles, Brain, CheckCircle, Clock, Layers
} from "lucide-react";

interface ResearchTemplate {
  id: string;
  name: string;
  description: string;
  icon: any;
  color: string;
  estimatedTime: string;
  steps: {
    title: string;
    description: string;
    suggestedPrompts: string[];
  }[];
  suggestedTopics: string[];
  category: "business" | "academic" | "personal" | "technical";
}

const templates: ResearchTemplate[] = [
  {
    id: "competitor-analysis",
    name: "Competitor Analysis",
    description: "Systematically analyze competitors to understand market positioning, strengths, and opportunities.",
    icon: Target,
    color: "text-blue-500",
    estimatedTime: "2-3 hours",
    category: "business",
    suggestedTopics: ["Market Position", "Product Features", "Pricing Strategy", "Marketing Approach", "SWOT Analysis"],
    steps: [
      {
        title: "Identify Key Competitors",
        description: "List direct and indirect competitors in your market space.",
        suggestedPrompts: [
          "Who are the top 5 competitors in [industry]?",
          "What companies offer similar solutions to [product/service]?",
          "Which emerging startups could disrupt [market]?"
        ]
      },
      {
        title: "Analyze Product/Service Offerings",
        description: "Compare features, pricing, and value propositions.",
        suggestedPrompts: [
          "Compare the features of [Competitor A] vs [Competitor B]",
          "What unique value proposition does [competitor] offer?",
          "How does [competitor] price their services?"
        ]
      },
      {
        title: "Study Marketing & Positioning",
        description: "Understand how competitors position themselves and reach customers.",
        suggestedPrompts: [
          "What marketing channels does [competitor] use?",
          "How does [competitor] position their brand?",
          "What is [competitor's] content strategy?"
        ]
      },
      {
        title: "Identify Strengths & Weaknesses",
        description: "Create a SWOT analysis for each competitor.",
        suggestedPrompts: [
          "What are the key strengths of [competitor]?",
          "What weaknesses can be identified in [competitor's] approach?",
          "What opportunities exist against [competitor]?"
        ]
      },
      {
        title: "Synthesize & Action Plan",
        description: "Draw conclusions and define your competitive strategy.",
        suggestedPrompts: [
          "Based on competitor analysis, what differentiators should we focus on?",
          "What gaps in the market can we exploit?",
          "Summarize key learnings and recommended actions"
        ]
      }
    ]
  },
  {
    id: "literature-review",
    name: "Literature Review",
    description: "Conduct a thorough review of existing research and publications on a topic.",
    icon: BookOpen,
    color: "text-green-500",
    estimatedTime: "4-6 hours",
    category: "academic",
    suggestedTopics: ["Key Theories", "Research Methods", "Findings Summary", "Research Gaps", "Future Directions"],
    steps: [
      {
        title: "Define Research Questions",
        description: "Clearly articulate what you want to learn from the literature.",
        suggestedPrompts: [
          "What are the key research questions about [topic]?",
          "What aspects of [topic] need to be explored?",
          "What is the scope of this literature review?"
        ]
      },
      {
        title: "Search & Collect Sources",
        description: "Gather relevant academic papers, books, and articles.",
        suggestedPrompts: [
          "What are the seminal papers on [topic]?",
          "Who are the key researchers in [field]?",
          "What are the most cited works about [topic]?"
        ]
      },
      {
        title: "Analyze & Categorize",
        description: "Organize sources by themes, methodologies, or chronology.",
        suggestedPrompts: [
          "What are the main themes in [topic] research?",
          "How have research methods evolved in [field]?",
          "What are the contrasting viewpoints on [topic]?"
        ]
      },
      {
        title: "Synthesize Findings",
        description: "Identify patterns, agreements, and disagreements in the literature.",
        suggestedPrompts: [
          "What do researchers agree about regarding [topic]?",
          "What are the main debates in [field]?",
          "How do findings from different studies compare?"
        ]
      },
      {
        title: "Identify Gaps & Conclusions",
        description: "Note areas that need more research and draw conclusions.",
        suggestedPrompts: [
          "What gaps exist in current [topic] research?",
          "What future research directions are suggested?",
          "Summarize the current state of knowledge on [topic]"
        ]
      }
    ]
  },
  {
    id: "market-research",
    name: "Market Research",
    description: "Investigate market size, trends, and customer needs for business decisions.",
    icon: TrendingUp,
    color: "text-purple-500",
    estimatedTime: "3-4 hours",
    category: "business",
    suggestedTopics: ["Market Size", "Customer Segments", "Industry Trends", "Growth Drivers", "Market Entry"],
    steps: [
      {
        title: "Define Market Scope",
        description: "Clearly define the market you're analyzing.",
        suggestedPrompts: [
          "What is the TAM/SAM/SOM for [market]?",
          "How is [market] defined and segmented?",
          "What are the geographic boundaries of [market]?"
        ]
      },
      {
        title: "Analyze Market Trends",
        description: "Identify current trends and future projections.",
        suggestedPrompts: [
          "What are the key trends shaping [industry]?",
          "How is technology impacting [market]?",
          "What regulatory changes affect [industry]?"
        ]
      },
      {
        title: "Understand Customer Needs",
        description: "Research target customer segments and their pain points.",
        suggestedPrompts: [
          "Who are the key customer segments in [market]?",
          "What problems do customers in [market] face?",
          "What do customers value most in [product category]?"
        ]
      },
      {
        title: "Assess Market Dynamics",
        description: "Analyze growth drivers, barriers, and competitive landscape.",
        suggestedPrompts: [
          "What drives growth in [market]?",
          "What are the barriers to entry in [industry]?",
          "How competitive is [market]?"
        ]
      },
      {
        title: "Develop Insights & Recommendations",
        description: "Synthesize findings into actionable insights.",
        suggestedPrompts: [
          "What opportunities exist in [market]?",
          "What is the optimal market entry strategy?",
          "Summarize key market insights and recommendations"
        ]
      }
    ]
  },
  {
    id: "problem-investigation",
    name: "Problem Investigation",
    description: "Systematically investigate and understand a problem before proposing solutions.",
    icon: FileSearch,
    color: "text-orange-500",
    estimatedTime: "1-2 hours",
    category: "personal",
    suggestedTopics: ["Problem Definition", "Root Causes", "Impact Analysis", "Stakeholders", "Solution Criteria"],
    steps: [
      {
        title: "Define the Problem",
        description: "Clearly articulate what the problem is and its scope.",
        suggestedPrompts: [
          "What exactly is the problem we're trying to solve?",
          "Who is affected by this problem?",
          "What are the symptoms of this problem?"
        ]
      },
      {
        title: "Gather Context",
        description: "Collect background information and data about the problem.",
        suggestedPrompts: [
          "When did this problem first appear?",
          "What has changed that might have caused this?",
          "What data do we have about the problem?"
        ]
      },
      {
        title: "Root Cause Analysis",
        description: "Dig deeper to understand the underlying causes.",
        suggestedPrompts: [
          "What are the root causes of [problem]?",
          "Use the 5 Whys technique on [problem]",
          "What contributing factors lead to [problem]?"
        ]
      },
      {
        title: "Analyze Impact",
        description: "Understand the consequences and urgency of the problem.",
        suggestedPrompts: [
          "What is the business impact of [problem]?",
          "How urgent is solving [problem]?",
          "What happens if we don't solve [problem]?"
        ]
      },
      {
        title: "Define Solution Criteria",
        description: "Establish what a good solution should achieve.",
        suggestedPrompts: [
          "What would success look like for solving [problem]?",
          "What constraints must a solution work within?",
          "What are the must-have vs nice-to-have criteria?"
        ]
      }
    ]
  },
  {
    id: "learning-path",
    name: "Learning Path",
    description: "Create a structured approach to learning a new skill or subject.",
    icon: Lightbulb,
    color: "text-yellow-500",
    estimatedTime: "1-2 hours",
    category: "personal",
    suggestedTopics: ["Fundamentals", "Core Concepts", "Practice Projects", "Resources", "Assessment"],
    steps: [
      {
        title: "Define Learning Goals",
        description: "Clarify what you want to achieve and why.",
        suggestedPrompts: [
          "What do I want to be able to do after learning [topic]?",
          "Why is learning [skill] important for me?",
          "What level of proficiency do I want to reach?"
        ]
      },
      {
        title: "Assess Current Knowledge",
        description: "Understand your starting point and any prerequisites.",
        suggestedPrompts: [
          "What do I already know about [topic]?",
          "What prerequisites do I need for [topic]?",
          "What are my learning strengths and challenges?"
        ]
      },
      {
        title: "Map the Learning Journey",
        description: "Break down the subject into manageable modules.",
        suggestedPrompts: [
          "What are the key concepts I need to learn in [topic]?",
          "In what order should I learn [topic] concepts?",
          "How can I structure my learning over [timeframe]?"
        ]
      },
      {
        title: "Identify Resources",
        description: "Find the best learning materials and tools.",
        suggestedPrompts: [
          "What are the best resources for learning [topic]?",
          "What books/courses are recommended for [skill]?",
          "What tools or practice environments do I need?"
        ]
      },
      {
        title: "Plan Practice & Application",
        description: "Design hands-on projects and ways to apply learning.",
        suggestedPrompts: [
          "What projects can I build to practice [skill]?",
          "How can I apply [topic] to real problems?",
          "What milestones should I set for my learning?"
        ]
      }
    ]
  },
  {
    id: "user-research",
    name: "User Research",
    description: "Understand user needs, behaviors, and pain points for product development.",
    icon: Users,
    color: "text-pink-500",
    estimatedTime: "3-5 hours",
    category: "technical",
    suggestedTopics: ["User Personas", "Journey Maps", "Pain Points", "User Needs", "Feature Ideas"],
    steps: [
      {
        title: "Define Research Objectives",
        description: "Clarify what you want to learn about your users.",
        suggestedPrompts: [
          "What do we need to understand about our users?",
          "What decisions will this research inform?",
          "Who are the target users for this research?"
        ]
      },
      {
        title: "Create User Personas",
        description: "Develop representative profiles of your target users.",
        suggestedPrompts: [
          "What are the different user segments for [product]?",
          "What are the demographics of [user type]?",
          "What motivates [user type] to use [product]?"
        ]
      },
      {
        title: "Map User Journeys",
        description: "Document how users interact with your product/service.",
        suggestedPrompts: [
          "What steps do users take to [complete task]?",
          "Where do users experience friction in [journey]?",
          "What touchpoints exist in [user journey]?"
        ]
      },
      {
        title: "Identify Pain Points & Needs",
        description: "Understand user frustrations and unmet needs.",
        suggestedPrompts: [
          "What frustrates users about [current solution]?",
          "What do users wish they could do with [product]?",
          "What workarounds have users developed?"
        ]
      },
      {
        title: "Synthesize Into Insights",
        description: "Transform findings into actionable product insights.",
        suggestedPrompts: [
          "What are the key insights from user research?",
          "What features would address user needs?",
          "What should we prioritize based on user feedback?"
        ]
      }
    ]
  }
];

export default function ResearchTemplates() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [selectedTemplate, setSelectedTemplate] = useState<ResearchTemplate | null>(null);
  const [projectName, setProjectName] = useState("");
  const [projectDescription, setProjectDescription] = useState("");

  const createSpaceMutation = useMutation({
    mutationFn: async (data: { name: string; description: string; goals: string[]; templateId: string }) => {
      return await mastermindSpacesService.createMastermindSpace({
        name: data.name,
        description: data.description,
        goals: data.goals,
        agents: [],
        linkedSourceIds: [],
        milestones: [],
      });
    },
    onSuccess: async (data) => {
      // Invalidate all spaces queries
      queryClient.invalidateQueries({ queryKey: ["spaces"] }); // For AppSidebar
      queryClient.invalidateQueries({ queryKey: ["/api/spaces"] }); // For spaces page
      toast({
        title: "Research Project Created",
        description: "Your research space has been set up with the template structure.",
      });
      setSelectedTemplate(null);
      setProjectName("");
      setProjectDescription("");
      setLocation(`/spaces/${data.id}`);
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to create research project. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleStartTemplate = () => {
    if (!selectedTemplate || !projectName.trim()) return;

    const goals = selectedTemplate.steps.map(step => step.title);
    
    createSpaceMutation.mutate({
      name: projectName.trim(),
      description: projectDescription.trim() || `Research project using ${selectedTemplate.name} template`,
      goals,
      templateId: selectedTemplate.id,
    });
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case "business": return "bg-blue-500/10 text-blue-500";
      case "academic": return "bg-green-500/10 text-green-500";
      case "personal": return "bg-orange-500/10 text-orange-500";
      case "technical": return "bg-purple-500/10 text-purple-500";
      default: return "bg-muted text-muted-foreground";
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold mb-2 flex items-center gap-3" data-testid="text-page-title">
          <Brain className="h-8 w-8 text-primary" />
          Research Templates
        </h1>
        <p className="text-muted-foreground">
          Start your research with proven frameworks and guided workflows. Choose a template to create a structured research project.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {templates.map((template) => {
          const Icon = template.icon;
          return (
            <Card key={template.id} className="hover-elevate cursor-pointer" onClick={() => setSelectedTemplate(template)} data-testid={`card-template-${template.id}`}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Icon className={`h-6 w-6 ${template.color}`} />
                  </div>
                  <Badge className={getCategoryColor(template.category)}>
                    {template.category}
                  </Badge>
                </div>
                <CardTitle className="mt-4">{template.name}</CardTitle>
                <CardDescription>{template.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Layers className="h-4 w-4" />
                    {template.steps.length} steps
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    {template.estimatedTime}
                  </div>
                </div>
              </CardContent>
              <CardFooter>
                <Button variant="outline" className="w-full gap-2">
                  <Sparkles className="h-4 w-4" />
                  Start Research
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </CardFooter>
            </Card>
          );
        })}
      </div>

      <Dialog open={!!selectedTemplate} onOpenChange={() => setSelectedTemplate(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedTemplate && <selectedTemplate.icon className={`h-5 w-5 ${selectedTemplate.color}`} />}
              {selectedTemplate?.name}
            </DialogTitle>
            <DialogDescription>
              {selectedTemplate?.description}
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="flex-1 pr-4">
            <div className="space-y-6">
              <div className="space-y-4">
                <div>
                  <Label htmlFor="project-name">Project Name</Label>
                  <Input
                    id="project-name"
                    value={projectName}
                    onChange={(e) => setProjectName(e.target.value)}
                    placeholder="e.g., Q4 Market Research"
                    className="mt-1"
                    data-testid="input-project-name"
                  />
                </div>
                <div>
                  <Label htmlFor="project-description">Description (Optional)</Label>
                  <Textarea
                    id="project-description"
                    value={projectDescription}
                    onChange={(e) => setProjectDescription(e.target.value)}
                    placeholder="Brief description of your research goals..."
                    className="mt-1"
                    rows={2}
                    data-testid="input-project-description"
                  />
                </div>
              </div>

              <div>
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-primary" />
                  Research Steps
                </h4>
                <div className="space-y-4">
                  {selectedTemplate?.steps.map((step, i) => (
                    <div key={i} className="p-4 bg-muted/50 rounded-lg">
                      <div className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-xs font-medium shrink-0">
                          {i + 1}
                        </div>
                        <div className="flex-1">
                          <h5 className="font-medium">{step.title}</h5>
                          <p className="text-sm text-muted-foreground mt-1">{step.description}</p>
                          <div className="mt-2 space-y-1">
                            <p className="text-xs font-medium text-muted-foreground">Suggested prompts:</p>
                            {step.suggestedPrompts.slice(0, 2).map((prompt, j) => (
                              <p key={j} className="text-xs text-muted-foreground italic">
                                "{prompt}"
                              </p>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <Layers className="h-4 w-4 text-primary" />
                  Suggested Topics
                </h4>
                <div className="flex flex-wrap gap-2">
                  {selectedTemplate?.suggestedTopics.map((topic, i) => (
                    <Badge key={i} variant="secondary">{topic}</Badge>
                  ))}
                </div>
              </div>
            </div>
          </ScrollArea>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setSelectedTemplate(null)}>
              Cancel
            </Button>
            <Button 
              onClick={handleStartTemplate}
              disabled={!projectName.trim() || createSpaceMutation.isPending}
              data-testid="button-start-project"
            >
              {createSpaceMutation.isPending ? "Creating..." : "Start Research Project"}
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
